import { Box, Button, Collapse, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { getAllInvoiceAddress } from 'src/shared/services/invoiceAddressService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { ControlledSingleAutoCompleteInvoiceAddress } from 'src/shared/components/form/controlledSingleAutoCompleteInvoiceAddress';
import useDebounce from 'src/shared/hooks/useDebounce';
import { updateInvoiceAddressOnMember } from 'src/shared/services/memberService';
import { toast } from 'react-toastify';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Member } from '../../models/member';
import { useSchemaEditPickFromRegister } from '../schemas/useSchemaEditPickFromRegister';
import { InvoiceAddressResponse } from '../models/invoiceAddressResponse';
import { InvoiceAddressCreateForm } from './invoiceAddressCreateForm';
import { InvoiceAddressRequest } from '../models/invoiceAddressRequest';

type InvoiceAddressHandleModalFromRegisterProps = {
  modalOpen: boolean;
  onClose: () => void;
  onStartOver: () => void;
  member: Member;
};

export const InvoiceAddressHandleModalFromRegister = (props: InvoiceAddressHandleModalFromRegisterProps) => {
  const { modalOpen, onClose, onStartOver, member } = props;

  const isMounted = useIsMounted();

  const { setDateStamp } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<InvoiceAddressResponse[]>();
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounce(query, 300);

  const fetchData = useCallback(async () => {
    try {
      const result = await getAllInvoiceAddress({
        filter: debouncedQuery,
        pageIndex: 0,
        pageSize: 1000,
        sortCol: 'invoiceAddress_id',
        sortDir: 'desc',
      });
      if (isMounted()) {
        const associationsResult = result.data.results.map((x) => {
          return { ...x, id: x.invoiceAddress_id };
        });
        ConsoleHelper.log(associationsResult);

        setTimeout(() => {
          setOptions(associationsResult);
        }, 100);
      }
    } catch (error) {
      if (isMounted()) {
        setLoading(false);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, debouncedQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pickInvoiceAddressFromPicker = useSchemaEditPickFromRegister();
  type EditInvoiceAddressFromPickerFormInterface = InferType<typeof pickInvoiceAddressFromPicker>;

  const formMethods = useForm<EditInvoiceAddressFromPickerFormInterface>({
    resolver: yupResolver(pickInvoiceAddressFromPicker),
    defaultValues: member.invoiceAddress && {
      invoiceAddress: member.invoiceAddress,
      name: member.invoiceAddress?.name,
      address: member.invoiceAddress?.address,
      postalcode: member.invoiceAddress?.postalcode,
      city: member.invoiceAddress?.city,
      country: member.invoiceAddress?.country,
      address2: member.invoiceAddress?.address2,
      email: member.invoiceAddress?.email,
      name2: member.invoiceAddress?.name2,
      invoiceReference: member.invoiceReference ?? '',
    },
  });

  const { handleSubmit, watch, setValue } = formMethods;

  const pickedItem = watch('invoiceAddress') !== undefined;

  const handleOptionSelect = useCallback(
    (option: InvoiceAddressResponse) => {
      setValue('name', option.name || '');
      setValue('name2', option.name2 || '');
      setValue('address', option.address || '');
      setValue('address2', option.address2 || '');
      setValue('postalcode', option.postalcode || '');
      setValue('city', option.city || '');
      setValue('country', option.country || '');
      setValue('email', option.email || '');
    },
    [setValue],
  );
  const saveFormData = useCallback(
    async (data: EditInvoiceAddressFromPickerFormInterface) => {
      setLoading(true);
      const dataToPost: InvoiceAddressRequest = {
        invoiceAddress_id: data.invoiceAddress.invoiceAddress_id,
        name: data.name,
        address: data.address,
        postalcode: data.postalcode,
        city: data.city,
        country: data.country,
        address2: data.address2,
        email: data.email,
        name2: data.name2,
        invoiceReference: data.invoiceReference,
      };
      try {
        await updateInvoiceAddressOnMember(member.member_id || 0, dataToPost);
        if (isMounted()) {
          toast.success(t('member:invoiceAddress.saveSuccess'));
          setDateStamp(new Date());
          onClose();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, member.member_id, onClose, setDateStamp],
  );

  const onSubmit = useCallback(
    (data: EditInvoiceAddressFromPickerFormInterface) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => onClose()}
      onSave={() => ConsoleHelper.log('save')}
      dialogTitle={t('member:invoiceAddress.pickAddress')}
      padding={0}
      maxWidth='sm'
      dialogContent={
        <Box
          sx={{
            position: 'relative',
            minHeight: '15rem',
            p: 4,
          }}
        >
          <LoadingOverlay loading={loading} />
          <Box sx={{}}>
            <GridWrapper>
              <Grid item xs={12}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography>{t('member:invoiceAddress.enterReference')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField name='invoiceReference' required label={t('member:invoiceAddress.reference')} />
                      </Grid>
                      <Grid item xs={12} mt={2}>
                        <Typography>{t('member:invoiceAddress.pageHeader')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledSingleAutoCompleteInvoiceAddress
                          name='invoiceAddress'
                          label={t('member:invoiceAddress.pageHeader')}
                          options={options ?? []}
                          setQuery={setQuery}
                          onOptionSelect={handleOptionSelect}
                        />
                      </Grid>
                      <Collapse timeout={{ appear: 1000, enter: 500, exit: 700 }} in={pickedItem}>
                        <Grid container spacing={2} pl={2} mt={2}>
                          <InvoiceAddressCreateForm disabled />
                        </Grid>
                      </Collapse>
                    </Grid>
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
      dialogActions={
        <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
          <Button disableElevation variant='outlined' color='primary' onClick={onStartOver} disabled={loading} sx={{ mr: 1 }}>
            {t('common:button.startOver')}
          </Button>
          <Button disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
            {t('common:button.save')}
          </Button>
        </Box>
      }
    />
  );
};
